import type { MarketDataRecord } from '../services/marketDataService';

export type LtipFormStat = {
  label: string;
  count: number;
  percentage: number;
};

/** Значения «Форма LTIP», означающие отсутствие LTIP. */
const ABSENT_LTIP_FORMS = new Set([
  'нет',
  'no',
  '-',
  '—',
  '–',
  'n/a',
  'na',
  'none',
  'отсутствует',
  'not applicable',
]);

export function isAbsentLtipForm(value: string | null | undefined): boolean {
  if (value == null) return true;
  const s = String(value).trim();
  if (!s) return true;
  return ABSENT_LTIP_FORMS.has(s.toLowerCase());
}

/** Форма LTIP из записи (поддерживает snake_case и camelCase из API). */
export function getRecordLtipForm(
  record: Pick<MarketDataRecord, 'ltip_form' | 'ltip'> & { ltipForm?: string | null },
): string | null {
  const raw =
    record.ltip_form ??
    record.ltipForm ??
    null;
  if (raw == null) return null;
  const form = String(raw).trim();
  if (isAbsentLtipForm(form)) return null;
  return form;
}

export function recordHasLtipForm(
  record: Pick<MarketDataRecord, 'ltip_form' | 'ltip'> & { ltipForm?: string | null },
): boolean {
  return getRecordLtipForm(record) != null;
}

/** @deprecated Используйте recordHasLtipForm для дэшборда форм LTIP */
export function recordHasLtip(record: Pick<MarketDataRecord, 'ltip'>): boolean {
  return record.ltip?.trim().toLowerCase() === 'да';
}

/** Доли форм LTIP среди записей с указанной формой. */
export function computeLtipFormStats(
  records: Array<Pick<MarketDataRecord, 'ltip_form' | 'ltip'> & { ltipForm?: string | null }>,
): LtipFormStat[] {
  const forms = records
    .map(getRecordLtipForm)
    .filter((form): form is string => form != null);

  if (forms.length === 0) return [];

  const counts = new Map<string, number>();
  for (const label of forms) {
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }

  const total = forms.length;
  return Array.from(counts.entries())
    .map(([label, count]) => ({
      label,
      count,
      percentage: (count / total) * 100,
    }))
    .sort((a, b) => b.percentage - a.percentage || a.label.localeCompare(b.label, 'ru'));
}

export function countRecordsWithLtipForm(
  records: Array<Pick<MarketDataRecord, 'ltip_form' | 'ltip'> & { ltipForm?: string | null }>,
): number {
  return records.filter(recordHasLtipForm).length;
}
